/**
 * Het contextmenu op een relatie: lijnsoort, routing, richting, knikpunten.
 *
 * Stond als tweehonderdzeventig regels JSX midden in `Canvas.tsx` en was
 * daardoor alleen op het React-Flow-canvas te krijgen. Het banen-canvas heeft
 * hetzelfde menu nodig — dezelfde muisknop hoort overal hetzelfde te doen —
 * en twee kopieën lopen uiteen. Vandaar één component.
 *
 * `edgeId` is het VOORKOMEN (de viewState-sleutel) en stuurt alles wat
 * per-voorkomen is: routing, hoeken, knikpunten. `relationId` is de relatie
 * zelf en stuurt wat over het model gaat: type, richting, omkeren.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  alwaysAllowedRelationTypeIds,
  getRegistry,
  resolveBilingual,
  validRelationTypesBetween,
} from "../../registry";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";

export interface RelatieMenuStand {
  x: number;
  y: number;
  /** De viewState-sleutel van het aangeklikte voorkomen. */
  edgeId: string;
  /** De relatie zelf. */
  relationId: string;
}

export function RelationContextMenu({
  stand,
  workspaceId,
  branchId,
  onClose,
  onSamenvoegen,
}: {
  stand: RelatieMenuStand;
  workspaceId: string | null;
  branchId: string | null;
  onClose: () => void;
  /** Meerdere relaties samenvoegen — alleen het React-Flow-canvas kent die
   *  bulk-selectie, dus die levert de actie. */
  onSamenvoegen?: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const routingMenu = stand;
  const setRoutingMenu = (v: RelatieMenuStand | null) => {
    if (v === null) onClose();
  };
  const routingMenuRef = useRef<HTMLDivElement | null>(null);
  const selectedRelationIds = useUi((st) => st.selectedRelationIds);
  const mergeSelectedRelations = onSamenvoegen ?? (() => {});

  // Buiten het menu klikken sluit 'm — zelfde gedrag als op het gewone canvas.
  useEffect(() => {
    const opKlik = (e: MouseEvent) => {
      if (e.target instanceof Node && routingMenuRef.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("mousedown", opKlik);
    return () => window.removeEventListener("mousedown", opKlik);
  }, [onClose]);

  const ws = useWorkspaceData.getState();
  const relation = ws.store.getRelation(routingMenu.relationId);
  const source = relation ? ws.store.getConcept(relation.sourceConceptId) : null;
  const target = relation ? ws.store.getConcept(relation.targetConceptId) : null;
  const matrixForward =
    source && target ? validRelationTypesBetween(source.typeId, target.typeId) : [];
  const reg = getRegistry();
  const activeDiagram = ws.activeDiagramId ? ws.store.getDiagram(ws.activeDiagramId) : null;
  const vk = activeDiagram ? reg.getViewKind(activeDiagram.viewKind) : null;
  // Tekenrelaties staan boven de view-kind-restrictie (zelfde rationale
  // als de tekenelementen). Zonder deze uitzondering bood het menu op
  // een procesmap alleen Serving en Association aan, ook op een lijn
  // die uit een tekenrechthoek vertrekt — precies waar je 'm naar een
  // vrije lijn zou willen omzetten (gemeld 2026-09-02).
  // Tekenrelaties staan boven de view-kind-restrictie — maar alleen op een
  // diagram waar je überhaupt kunt tekenen. Een banendiagram laat geen
  // tekenelementen toe, dus een vrije lijn heeft er niets om aan vast te
  // zitten en hoort niet in dit menu te staan.
  const magTekenen =
    !vk?.included_types ||
    vk.included_types.length === 0 ||
    vk.included_types.some((id) => id.startsWith("drawing."));
  const viewAllow =
    vk?.included_relation_types && vk.included_relation_types.length > 0
      ? new Set([
          ...vk.included_relation_types,
          ...(magTekenen ? alwaysAllowedRelationTypeIds() : []),
        ])
      : null;
  const allowedTypes = viewAllow
    ? matrixForward.filter((id) => viewAllow.has(id) || id === relation?.typeId)
    : matrixForward;
  // Bestaande type áltijd in de lijst (ook als matrix 'm sinds
  // creatie heeft uitgesloten — bv. na concept-type-rename), zodat
  // gebruiker 'm tenminste herkent.
  if (relation && !allowedTypes.includes(relation.typeId)) {
    allowedTypes.unshift(relation.typeId);
  }
  const labelFor = (id: string) => {
    const rt = reg.getRelationType(id);
    return rt ? resolveBilingual(rt.label, lang) : id;
  };
  // Omgekeerde richting: relatie-types geldig voor target→source.
  // Kiezen swapt de endpoints (+ viewState-hoeken), net als in het
  // attributen-paneel.
  const matrixReverse =
    source && target ? validRelationTypesBetween(target.typeId, source.typeId) : [];
  const reverseTypes = viewAllow
    ? matrixReverse.filter((id) => viewAllow.has(id) || id === relation?.typeId)
    : matrixReverse;
  const isAssoc = relation?.typeId === "archimate.Association";
  const directed = relation?.attributes?.directed === true;
  const reverseTo = (typeId: string) => {
    if (!workspaceId || !branchId || !relation) return;
    const ws2 = useWorkspaceData.getState();
    void ws2.patchRelation(workspaceId, branchId, relation.id, {
      type_id: typeId,
      source_concept_id: relation.targetConceptId,
      target_concept_id: relation.sourceConceptId,
    });
    const diagramId = ws2.activeDiagramId;
    if (diagramId) {
      // Hoek/handle-state is per-instance visueel — hoort bij de
      // aangeklikte entry (`routingMenu.edgeId`), niet bij de
      // canonical relationId (die op een multi-instance edge een
      // andere entry is, of helemaal geen entry op dit diagram).
      const ve = ws2.store.getDiagram(diagramId)?.viewState[routingMenu.edgeId];
      ws2.setRelationAnglesOnCanvas(
        workspaceId,
        branchId,
        routingMenu.edgeId,
        ve?.targetAngle ?? null,
        ve?.sourceAngle ?? null,
      );
      if (ve?.sourceHandle || ve?.targetHandle) {
        ws2.setRelationHandlesOnCanvas(workspaceId, branchId, routingMenu.edgeId, null, null);
      }
    }
    setRoutingMenu(null);
  };
  const toggleDirected = () => {
    if (!workspaceId || !branchId || !relation) return;
    void useWorkspaceData.getState().patchRelation(workspaceId, branchId, relation.id, {
      attributes: { ...relation.attributes, directed: !directed },
    });
    setRoutingMenu(null);
  };
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationeel canvas-oppervlak; interactie is pointer-gebaseerd, canvas-keyboard-nav zit op React-Flow-niveau
    <div
      ref={routingMenuRef}
      className="ledger absolute z-20 min-w-[200px] rounded-md border border-(--ld-control) bg-(--ld-surface) py-1 text-sm shadow-xl ring-1 ring-(--ld-border)"
      style={{ left: routingMenu.x, top: routingMenu.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* ── Samenvoegen (≥2 kloon-relaties geselecteerd) ────── */}
      {onSamenvoegen && new Set(selectedRelationIds).size >= 2 && (
        <>
          <button
            type="button"
            className="block w-full px-3 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)"
            onClick={mergeSelectedRelations}
          >
            {t("canvas.contextMenu.merge")} ({new Set(selectedRelationIds).size})
          </button>
          <div className="my-1 border-(--ld-border) border-t" />
        </>
      )}
      {/* ── Relatie-type ────────────────────────────────────── */}
      {(allowedTypes.length > 0 || reverseTypes.length > 0 || isAssoc) && (
        <>
          <div className="px-3 py-1 text-(--ld-muted) text-xs">
            {t("canvas.relationMenu.relationType")}
          </div>
          {allowedTypes.map((typeId) => {
            const isCurrent = relation?.typeId === typeId;
            return (
              <button
                key={`type-${typeId}`}
                type="button"
                className={`block w-full px-3 py-1 text-left hover:bg-(--ld-raised) ${
                  isCurrent ? "font-semibold text-(--ld-text)" : "text-(--ld-text)"
                }`}
                onClick={() => {
                  if (isCurrent || !workspaceId || !branchId) {
                    setRoutingMenu(null);
                    return;
                  }
                  void useWorkspaceData
                    .getState()
                    .patchRelation(workspaceId, branchId, routingMenu.relationId, {
                      type_id: typeId,
                    });
                  setRoutingMenu(null);
                }}
              >
                {isCurrent ? `✓ ${labelFor(typeId)}` : labelFor(typeId)}
              </button>
            );
          })}
          {/* Omgekeerde richting — swapt endpoints */}
          {reverseTypes.length > 0 && (
            <>
              <div className="px-3 pt-1 text-(--ld-muted) text-xs">
                {t("canvas.relationMenu.reverseDirection")}
              </div>
              {reverseTypes.map((typeId) => (
                <button
                  key={`rev-${typeId}`}
                  type="button"
                  className="block w-full px-3 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)"
                  onClick={() => reverseTo(typeId)}
                >
                  {t("canvas.relationMenu.reversed", { label: labelFor(typeId) })}
                </button>
              ))}
            </>
          )}
          {/* Association: directed/undirected toggle */}
          {isAssoc && (
            <button
              type="button"
              className="block w-full px-3 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)"
              onClick={toggleDirected}
            >
              {directed ? t("canvas.relationMenu.directedOn") : t("canvas.relationMenu.directed")}
            </button>
          )}
          <div className="my-1 border-(--ld-border) border-t" />
        </>
      )}
      {/* ── Uiterlijk (alleen tekenlijnen) ──────────────────── */}
      {(() => {
        // Een tekenlijn mag het uiterlijk van een relatietype lenen —
        // streepjes, pijlpunt, ruit — zonder er één te worden. De
        // keuzelijst komt uit het pack zelf (`appearance`-attribuut op
        // drawing.Line / drawing.ArrowLine), zodat er één bron is voor
        // de waarden en hun labels.
        if (!relation?.typeId.startsWith("drawing.")) return null;
        const schema = getRegistry()
          .getRelationType(relation.typeId)
          ?.attributes?.find((a) => a.name === "appearance");
        const opties = schema?.options ?? [];
        if (opties.length === 0) return null;
        const huidig =
          typeof relation.attributes?.appearance === "string"
            ? relation.attributes.appearance
            : "own";
        const labelVoor = (waarde: string) => {
          const l = schema?.option_labels?.[waarde];
          return (l ? resolveBilingual(l, lang) : "") || waarde;
        };
        return (
          <>
            <div className="px-3 py-1 text-(--ld-muted) text-xs">
              {t("canvas.relationMenu.appearance")}
            </div>
            {opties.map((waarde) => {
              const isHuidig = huidig === waarde;
              return (
                <button
                  key={`app-${waarde}`}
                  type="button"
                  className={`block w-full px-3 py-1 text-left hover:bg-(--ld-raised) ${
                    isHuidig ? "font-semibold text-(--ld-text)" : "text-(--ld-text)"
                  }`}
                  onClick={() => {
                    if (isHuidig || !workspaceId || !branchId) {
                      setRoutingMenu(null);
                      return;
                    }
                    void useWorkspaceData
                      .getState()
                      .patchRelation(workspaceId, branchId, routingMenu.relationId, {
                        attributes: { ...relation.attributes, appearance: waarde },
                      });
                    setRoutingMenu(null);
                  }}
                >
                  {isHuidig ? `✓ ${labelVoor(waarde)}` : labelVoor(waarde)}
                </button>
              );
            })}
            <div className="my-1 border-(--ld-border) border-t" />
          </>
        );
      })()}
      {/* ── Lijn-routing ────────────────────────────────────── */}
      <div className="px-3 py-1 text-(--ld-muted) text-xs">{t("canvas.relationMenu.routing")}</div>
      {(
        [
          ["smoothstep", t("attributePanel.routing.smoothstep")],
          ["step", t("attributePanel.routing.step")],
          ["straight", t("attributePanel.routing.straight")],
          ["bezier", t("attributePanel.routing.bezier")],
          ["manual", t("attributePanel.routing.manual")],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          className="block w-full px-3 py-1 text-left hover:bg-(--ld-raised)"
          onClick={() => {
            if (!workspaceId || !branchId) return;
            useWorkspaceData
              .getState()
              .setRelationRoutingOnCanvas(workspaceId, branchId, routingMenu.edgeId, value);
            setRoutingMenu(null);
          }}
        >
          {label}
        </button>
      ))}
      <div className="my-1 border-(--ld-border) border-t" />
      <button
        type="button"
        className="block w-full px-3 py-1 text-left text-(--ld-muted) hover:bg-(--ld-raised)"
        onClick={() => {
          if (!workspaceId || !branchId) return;
          useWorkspaceData
            .getState()
            .setRelationWaypointsOnCanvas(workspaceId, branchId, routingMenu.edgeId, null);
          setRoutingMenu(null);
        }}
      >
        {t("canvas.relationMenu.clearWaypoints")}
      </button>
    </div>
  );
}
